import type { ReactElement } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { LiveVideoControls, useLivePlayback } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

import { cameraCaptureBlocker, localhostStudioHref } from './camera-capture.js';
import { STUDIO_CAMERA_CYCLE_EVENT } from './roxy-prefs.js';
import './BroadcasterPreview.css';

export interface BroadcasterPreviewProps {
  active: boolean;
  onStats?: (stats: BroadcasterPreviewStats) => void;
  onStream?: (stream: MediaStream | null) => void;
  fanStream?: MediaStream | null;
}

export interface BroadcasterPreviewStats {
  width: number;
  height: number;
  fps: number;
  hasAudio: boolean;
  hasVideo: boolean;
}

/**
 * Local camera preview; the MediaStream is published to fans over WebRTC while live.
 * getUserMedia requires a secure context (HTTPS or localhost) and often a click.
 */
export function BroadcasterPreview({ active, onStats, onStream, fanStream = null }: BroadcasterPreviewProps): ReactElement {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const fanVideoRef = useRef<HTMLVideoElement | null>(null);
  const [frameEl, setFrameEl] = useState<HTMLDivElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const onStatsRef = useRef(onStats);
  const onStreamRef = useRef(onStream);
  const [error, setError] = useState<string | null>(null);
  const [blocker, setBlocker] = useState<ReturnType<typeof cameraCaptureBlocker>>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [hasStream, setHasStream] = useState(false);
  const [micMuted, setMicMuted] = useState(false);
  const [fanVideoEl, setFanVideoEl] = useState<HTMLVideoElement | null>(null);
  const startingRef = useRef(false);
  const fanPlayback = useLivePlayback(fanVideoEl, false);

  useEffect(() => {
    onStatsRef.current = onStats;
    onStreamRef.current = onStream;
  }, [onStats, onStream]);

  const startCamera = useCallback(async (): Promise<void> => {
    if (streamRef.current || startingRef.current) {
      return;
    }

    const issue = cameraCaptureBlocker();
    if (issue) {
      setBlocker(issue);
      setError(null);
      return;
    }

    setBlocker(null);
    setIsStarting(true);
    startingRef.current = true;
    setError(null);
    const hung = window.setTimeout(() => {
      if (!streamRef.current) {
        startingRef.current = false;
        setIsStarting(false);
      }
    }, 8_000);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30 },
        },
        audio: true,
      });

      if (streamRef.current) {
        for (const track of stream.getTracks()) {
          track.stop();
        }
        return;
      }

      streamRef.current = stream;
      setHasStream(true);
      setMicMuted(false);
      onStreamRef.current?.(stream);
      const videoEl = videoRef.current;
      if (videoEl) {
        videoEl.srcObject = stream;
        await videoEl.play().catch(() => undefined);
      }
    } catch {
      setError(t('common.studio.live.previewDenied'));
      onStatsRef.current?.({ width: 0, height: 0, fps: 0, hasAudio: false, hasVideo: false });
    } finally {
      window.clearTimeout(hung);
      startingRef.current = false;
      setIsStarting(false);
    }
  }, []);

  useEffect(() => {
    if (!active) {
      stopStream(streamRef, videoRef);
      setHasStream(false);
      setMicMuted(false);
      onStreamRef.current?.(null);
      onStatsRef.current?.({ width: 0, height: 0, fps: 0, hasAudio: false, hasVideo: false });
      setError(null);
      setBlocker(null);
      return;
    }

    const issue = cameraCaptureBlocker();
    if (issue) {
      setBlocker(issue);
      return;
    }

    void startCamera();

    return () => {
      stopStream(streamRef, videoRef);
      setHasStream(false);
      onStreamRef.current?.(null);
    };
  }, [active, startCamera]);

  useEffect(() => {
    async function cycleCamera(): Promise<void> {
      const current = streamRef.current;
      if (!current) {
        return;
      }
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videos = devices.filter((device) => device.kind === 'videoinput' && device.deviceId);
      if (videos.length < 2) {
        return;
      }
      const currentId = current.getVideoTracks()[0]?.getSettings().deviceId;
      const index = Math.max(
        0,
        videos.findIndex((device) => device.deviceId === currentId),
      );
      const next = videos[(index + 1) % videos.length];
      if (!next) {
        return;
      }
      const nextStream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: { exact: next.deviceId },
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30 },
        },
        audio: true,
      });
      for (const track of current.getTracks()) {
        track.stop();
      }
      streamRef.current = nextStream;
      setHasStream(true);
      onStreamRef.current?.(nextStream);
      const videoEl = videoRef.current;
      if (videoEl) {
        videoEl.srcObject = nextStream;
        await videoEl.play().catch(() => undefined);
      }
    }

    function handleCycle(): void {
      void cycleCamera();
    }

    window.addEventListener(STUDIO_CAMERA_CYCLE_EVENT, handleCycle);
    return () => {
      window.removeEventListener(STUDIO_CAMERA_CYCLE_EVENT, handleCycle);
    };
  }, []);

  useEffect(() => {
    if (!hasStream) {
      return;
    }

    let cancelled = false;
    let frameCount = 0;
    let lastFpsAt = performance.now();
    let rafId = 0;
    let videoCallbackId: number | undefined;
    let videoElForFrames: HTMLVideoElement | null = null;
    const stream = streamRef.current;
    const videoTrack = stream?.getVideoTracks()[0] ?? null;
    const videoEl = videoRef.current;

    const tickFps = (): void => {
      if (cancelled || !stream) {
        return;
      }
      const now = performance.now();
      const elapsed = now - lastFpsAt;
      if (elapsed >= 1000) {
        const fps = Math.round((frameCount * 1000) / elapsed);
        frameCount = 0;
        lastFpsAt = now;
        const settings = videoTrack?.getSettings();
        onStatsRef.current?.({
          width: settings?.width ?? videoEl?.videoWidth ?? 0,
          height: settings?.height ?? videoEl?.videoHeight ?? 0,
          fps,
          hasAudio: stream
            .getAudioTracks()
            .some((track) => track.enabled && track.readyState === 'live'),
          hasVideo: Boolean(videoTrack && videoTrack.readyState === 'live'),
        });
      }
    };

    videoElForFrames = videoRef.current;
    if (videoElForFrames && 'requestVideoFrameCallback' in videoElForFrames) {
      const onFrame = (): void => {
        frameCount += 1;
        tickFps();
        if (videoElForFrames && !cancelled) {
          videoCallbackId = videoElForFrames.requestVideoFrameCallback(onFrame);
        }
      };
      videoCallbackId = videoElForFrames.requestVideoFrameCallback(onFrame);
    } else {
      const loop = (): void => {
        frameCount += 1;
        tickFps();
        rafId = window.requestAnimationFrame(loop);
      };
      rafId = window.requestAnimationFrame(loop);
    }

    return () => {
      cancelled = true;
      if (videoCallbackId !== undefined && videoElForFrames) {
        videoElForFrames.cancelVideoFrameCallback?.(videoCallbackId);
      }
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [hasStream]);

  useEffect(() => {
    const video = fanVideoRef.current;
    setFanVideoEl(video);
    if (!video) {
      return;
    }
    if (fanStream) {
      video.srcObject = fanStream;
      void video.play().catch(() => undefined);
      return;
    }
    video.srcObject = null;
  }, [fanStream]);

  const setMicEnabled = (enabled: boolean): void => {
    const stream = streamRef.current;
    if (!stream) {
      return;
    }
    for (const track of stream.getAudioTracks()) {
      track.enabled = enabled;
    }
    setMicMuted(!enabled);
  };

  const showEnable = active && !hasStream && !isStarting;
  const localhostUrl = localhostStudioHref();

  return (
    <div className="broadcaster-preview">
      <div className="broadcaster-preview__frame" ref={setFrameEl}>
        <video
          ref={videoRef}
          className="broadcaster-preview__video"
          playsInline
          muted
          autoPlay
        />
        {fanStream ? (
          <video
            ref={fanVideoRef}
            className="broadcaster-preview__c2c"
            playsInline
            autoPlay
            aria-label={t('common.studio.live.c2cPipLabel')}
          />
        ) : null}
        {!active ? (
          <p className="broadcaster-preview__overlay">{t('common.studio.live.previewIdle')}</p>
        ) : null}
        {active && isStarting ? (
          <p className="broadcaster-preview__overlay">{t('common.studio.live.previewStarting')}</p>
        ) : null}
        {showEnable ? (
          <div className="broadcaster-preview__overlay broadcaster-preview__overlay--actions">
            {blocker === 'insecure' ? (
              <p className="broadcaster-preview__copy">
                {t('common.studio.live.previewInsecure').replace('{{url}}', localhostUrl)}
              </p>
            ) : null}
            {blocker === 'unsupported' ? (
              <p className="broadcaster-preview__copy">{t('common.studio.live.previewUnsupported')}</p>
            ) : null}
            {error ? (
              <p className="broadcaster-preview__copy broadcaster-preview__copy--error">{error}</p>
            ) : null}
            {!blocker && !error ? (
              <p className="broadcaster-preview__copy">{t('common.studio.live.previewEnable')}</p>
            ) : null}
            {blocker === 'insecure' ? (
              <Button
                label={t('common.studio.live.previewInsecureCta')}
                onClick={() => {
                  window.location.assign(localhostUrl);
                }}
              />
            ) : (
              <Button
                label={error ? t('common.studio.live.previewTryAgain') : t('common.studio.live.previewEnable')}
                onClick={() => {
                  void startCamera();
                }}
              />
            )}
          </div>
        ) : null}
        {hasStream ? (
          <LiveVideoControls
            muted={fanPlayback.muted}
            volume={fanPlayback.volume}
            onMutedChange={fanPlayback.setMuted}
            onVolumeChange={fanPlayback.setVolume}
            showVolume={Boolean(fanStream)}
            fullscreenTarget={frameEl}
            extra={
              <button
                type="button"
                className={`fm-live-controls__btn${micMuted ? '' : ' fm-live-controls__btn--on'}`}
                aria-pressed={!micMuted}
                aria-label={micMuted ? t('common.livePlayer.micUnmute') : t('common.livePlayer.micMute')}
                onClick={() => {
                  setMicEnabled(micMuted);
                }}
              >
                {micMuted ? t('common.livePlayer.micUnmute') : t('common.livePlayer.micMute')}
              </button>
            }
          />
        ) : null}
      </div>
    </div>
  );
}

function stopStream(
  streamRef: { current: MediaStream | null },
  videoRef: { current: HTMLVideoElement | null },
): void {
  if (streamRef.current) {
    for (const track of streamRef.current.getTracks()) {
      track.stop();
    }
    streamRef.current = null;
  }
  if (videoRef.current) {
    videoRef.current.srcObject = null;
  }
}
